import {
  applyInventoryOp,
  type InvContext,
  type InventoryOp,
  type ProfileWire,
  type SkillId,
} from "@rietz/shared";
import type { GameSocket, InvResultMsg } from "../net/socket.js";
import { setContainer, setInventory, setProfile } from "../ui/store.js";
import type { OpIds } from "./opIds.js";

/**
 * The inventory as the client believes it — BETWEEN raids. The twin of
 * `game/inventory.ts`, smaller: one context, `{ inv: the KIT, container: the
 * STASH }`, pushed through the same shared `applyInventoryOp`, so the stash is
 * published through the store's `container` slot and `drag.ts`, `quickmove.ts`,
 * the menu, the tooltip and the Werkbank know nothing about the menu.
 *
 * Authoritative state comes from `profile`, whole, after every menu op,
 * purchase, sale and settlement. Displayed = authoritative + every unanswered
 * op; accepted ops are folded in on their `invResult`. Known: dragging faster
 * than the round trip can flicker once, since the profile replaces the state
 * wholesale — accepted, the menu is not a firefight.
 *
 * Buying, selling, quest handovers and the hideout verbs are NOT optimistic:
 * they move euros, mint ids or spend wall clock only the server owns. The
 * `opId` still comes from the shared counter but never becomes a pending op.
 */

/**
 * Where the stash is published. Not a world container: a menu `invOp` names the
 * area `"container"` and the server knows that means the stash.
 */
const STASH_ID = "stash";

/** English label for the stash panel's header. */
const STASH_NAME = "Stash";

/** One op the server has not answered yet. Mirrors the raid controller's. */
interface PendingMetaOp {
  opId: number;
  op: InventoryOp;
  /** Ids this op minted, so a replay reproduces them instead of re-keying the
   *  player's panel underneath them. See the raid controller's note. */
  readonly iids: string[];
}

export interface MetaInventoryControllerOptions {
  /** `null` offline: an `?offline=1` session has no meta layer at all. */
  socket: GameSocket | null;
  /** THE connection's op-id counter, shared with the raid controller. */
  opIds: OpIds;
}

export class MetaInventoryController {
  private readonly o: MetaInventoryControllerOptions;

  /** The last profile the server sent. Never guessed at. */
  private auth: ProfileWire | null = null;
  private readonly pending: PendingMetaOp[] = [];
  private displayed: InvContext | null = null;
  private localIidSeq = 0;

  /**
   * Whether the MENU owns the store's `inv`/`container` slots. A `profile`
   * arrives during a raid too (after `raidEnd`), so publishing unconditionally
   * would replace the corpse being looted. `main.ts` hands ownership over.
   */
  private active = false;

  constructor(opts: MetaInventoryControllerOptions) {
    this.o = opts;
  }

  /** The profile as last sent by the server, or `null` on a direct server. */
  get profile(): ProfileWire | null {
    return this.auth;
  }

  /**
   * Hands the store's inventory slots to the menu (or back). Publishing on the
   * way in refills what `InventoryController.reset()` cleared.
   */
  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    if (active) {
      this.publish();
      return;
    }
    // Handing back leaves the slots EMPTY. `main.ts` deactivates on `welcome`,
    // strictly before the raid's first `inv`.
    setInventory(null);
    setContainer(null);
  }

  // -----------------------------------------------------------------------
  // UiActions (the menu's half — `main.ts` routes by screen)
  // -----------------------------------------------------------------------

  /** One menu edit: kit ↔ stash, equip, split, attach. Optimistic. */
  tryOp(op: InventoryOp): void {
    const ctx = this.displayed;
    if (ctx === null) return;

    const p: PendingMetaOp = { opId: 0, op, iids: [] };
    const next = this.applyPending(ctx, p);
    // Refused against the state the player is looking at — nothing to predict,
    // and nothing worth an id.
    if (next === null) return;

    p.opId = this.o.opIds.next();
    this.pending.push(p);
    this.displayed = next;
    this.publish();
    this.o.socket?.sendInvOp(p.opId, op);
  }

  /**
   * Buy one offer. Not optimistic — see the file header. Affordability is the
   * SERVER's check; the shelf disables what cannot be paid for.
   */
  buy(traderId: string, defId: string): void {
    this.o.socket?.sendBuy(this.o.opIds.next(), traderId, defId);
  }

  /** Sell one instance out of the stash. Same non-optimism as `buy`. */
  sell(traderId: string, iid: string): void {
    this.o.socket?.sendSell(this.o.opIds.next(), traderId, iid);
  }

  /** Repair one plate or one gun. Same non-optimism as `buy` — the euros and the
   *  new durability both arrive in the `profile` that follows. */
  repair(iid: string): void {
    this.o.socket?.sendRepair(this.o.opIds.next(), iid);
  }

  /**
   * Hand chosen instances over against one `bringItems` quest. Not optimistic:
   * what it COSTS is decided by the server, which takes up to the remainder and
   * splits the last stack if it must.
   */
  questTurnIn(questId: string, iids: readonly string[]): void {
    this.o.socket?.sendQuestTurnIn(this.o.opIds.next(), questId, iids);
  }

  // --- The hideout: seven verbs, non-optimistic like `buy` and `sell` — euros,
  // minted instances, wall clock. The screen greys out what the server would
  // refuse (`buildProblem` / `craftProblem`), so a click here ought to succeed.

  /** Start the next level of one module. */
  hideoutBuild(moduleId: string): void {
    this.o.socket?.sendHideoutBuild(this.o.opIds.next(), moduleId);
  }

  /** Start one recipe. */
  hideoutCraft(craftId: string): void {
    this.o.socket?.sendHideoutCraft(this.o.opIds.next(), craftId);
  }

  /** Take a finished craft's output into the stash. */
  hideoutCollect(craftId: string): void {
    this.o.socket?.sendHideoutCollect(this.o.opIds.next(), craftId);
  }

  /** Empty a production module's buffer into the stash. */
  hideoutHarvest(moduleId: string): void {
    this.o.socket?.sendHideoutHarvest(this.o.opIds.next(), moduleId);
  }

  /** Feed one canister out of the stash into the generator's tank. */
  hideoutFuel(iid: string): void {
    this.o.socket?.sendHideoutFuel(this.o.opIds.next(), iid);
  }

  /** Flip the generator's switch. */
  hideoutPower(on: boolean): void {
    this.o.socket?.sendHideoutPower(this.o.opIds.next(), on);
  }

  /**
   * One finished gym session: the skill trained and the minigame score in [0,
   * 1]. Non-optimistic — the COOLDOWN is stamped from the server's clock.
   */
  hideoutTrain(skill: SkillId, score: number): void {
    this.o.socket?.sendHideoutTrain(this.o.opIds.next(), skill, score);
  }

  // -----------------------------------------------------------------------
  // Server messages
  // -----------------------------------------------------------------------

  /**
   * A fresh authoritative profile, always whole. Recorded and published whether
   * or not the menu owns the slots — the summary screen wants the euros.
   */
  onProfile(profile: ProfileWire): void {
    this.auth = profile;
    this.rebuild();
  }

  /**
   * The verdict on one menu op — the same message and id space as a raid op. An
   * unknown id is a purchase, sale or raid op: nothing to fold or roll back.
   */
  onInvResult(msg: InvResultMsg): void {
    const idx = this.pending.findIndex((p) => p.opId === msg.opId);
    if (idx < 0) return;
    const p = this.pending[idx] as PendingMetaOp;
    this.pending.splice(idx, 1);

    if (msg.ok && this.auth !== null) {
      // Folded into the authoritative copy: the op is now truth and the
      // `profile`
      // may be a message away. A failed fold is left out; the profile corrects
      // it.
      const folded = this.applyPending(this.contextOf(this.auth), p);
      if (folded !== null) this.auth = withContext(this.auth, folded);
    }
    this.rebuild();
  }

  /**
   * Disconnect: forget everything the old connection said, profile included —
   * the reconnect's `hello` is answered with a fresh one.
   */
  reset(): void {
    this.pending.length = 0;
    this.auth = null;
    this.rebuild();
  }

  // -----------------------------------------------------------------------
  // Internals
  // -----------------------------------------------------------------------

  /** The op context one profile describes: the kit, and the stash as "the open
   *  container" — which is exactly how the server applies a menu op. */
  private contextOf(profile: ProfileWire): InvContext {
    return {
      inv: profile.kit,
      container: { id: STASH_ID, grid: profile.stash },
      containerOwned: true,
    };
  }

  /** Mints a provisional instance id. Same `c` prefix, same reason: it can
   *  never collide with one the server picked. */
  private readonly mintIid = (): string => {
    this.localIidSeq += 1;
    return `m${this.localIidSeq}`;
  };

  private applyPending(ctx: InvContext, p: PendingMetaOp): InvContext | null {
    let i = 0;
    const nextIid = (): string => {
      const remembered = p.iids[i];
      i += 1;
      if (remembered !== undefined) return remembered;
      const minted = this.mintIid();
      p.iids.push(minted);
      return minted;
    };
    const res = applyInventoryOp(ctx, p.op, nextIid);
    return res.ok ? res.ctx : null;
  }

  /** Authoritative profile + every surviving pending op = what is on screen. */
  private rebuild(): void {
    const auth = this.auth;
    if (auth === null) {
      this.displayed = null;
      this.publish();
      return;
    }

    let ctx = this.contextOf(auth);
    for (let i = 0; i < this.pending.length; ) {
      const next = this.applyPending(ctx, this.pending[i] as PendingMetaOp);
      if (next === null) {
        // A guess about a world that turned out not to exist. Same answer as in
        // a raid: drop it and let the authoritative state stand.
        this.pending.splice(i, 1);
        continue;
      }
      ctx = next;
      i += 1;
    }
    this.displayed = ctx;
    this.publish();
  }

  /**
   * Pushes the displayed state into the store — once per change, never per
   * frame.
   */
  private publish(): void {
    setProfile(this.auth);
    if (!this.active) return;

    const ctx = this.displayed;
    setInventory(ctx?.inv ?? null);
    const stash = ctx?.container ?? null;
    setContainer(
      stash === null
        ? null
        : { id: STASH_ID, kind: "stash", name: STASH_NAME, grid: stash.grid },
    );
  }
}

/** A profile with its kit and stash taken from `ctx` — the shape `rebuild`
 *  needs after folding an accepted op into the authoritative copy. */
function withContext(profile: ProfileWire, ctx: InvContext): ProfileWire {
  return {
    ...profile,
    kit: ctx.inv,
    stash: ctx.container === null ? profile.stash : ctx.container.grid,
  };
}
